import { ArrowUpRight, X } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useEndpoint } from '../hooks/useEndpoints';
import { endpointClassLabel, endpointLabel } from '../utils/endpointLabel';
import { EndpointLinkSummary, EndpointLinkTraffic } from './EndpointLinks';
import { ManageEndpointButton } from './ManageEndpointButton';
import type { TrafficRange } from './traffic/range';

interface EndpointPanelProps {
  endpointId: string;
  trafficRange?: TrafficRange;
  onClose?: () => void;
  /** Open one of its links (`links[].linkId`). */
  onLinkSelect?: (endpointId: string, linkId: string) => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr>
      <th className="text-left text-xs font-medium text-muted-foreground py-1.5 w-2/5">{label}</th>
      <td className="py-1.5">{children}</td>
    </tr>
  );
}

/**
 * An endpoint on the map: what it is, and every place it is attached, each with the attached side's
 * traffic — an endpoint has few links, so they are all drawn here. A link seen only behind another
 * device has no chart: that port carries the neighbour's traffic, not this endpoint's. Each still
 * opens as a link on its own.
 */
export default function EndpointPanel({ endpointId, trafficRange, onClose, onLinkSelect }: EndpointPanelProps) {
  const { data: endpoint, isLoading } = useEndpoint(endpointId);
  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Loading endpoint…</p>
      </div>
    );
  }
  if (!endpoint) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Endpoint not found</p>
      </div>
    );
  }
  const links = endpoint.links ?? [];
  return (
    <div className="rounded-md border border-border bg-card p-4 endpoint-panel">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold truncate">{endpointLabel(endpoint)}</h3>
          <Link to={`/endpoints/${endpoint.id}`} className="inline-flex items-center gap-1 text-xs text-sev-5 hover:underline">
            View full details
            <ArrowUpRight className="h-3 w-3" />
          </Link>
          <div className="mt-2">
            <ManageEndpointButton endpoint={endpoint} />
          </div>
        </div>
        {onClose ? (
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClose} aria-label="Close">
            <X className="w-3.5 h-3.5" />
          </Button>
        ) : null}
      </div>
      <table className="w-full text-sm mb-4">
        <tbody className="divide-y divide-border-subtle">
          <Row label="MAC">
            <code className="font-mono text-xs">{endpoint.macAddress}</code>
          </Row>
          <Row label="Vendor">{endpoint.ouiVendor ?? '—'}</Row>
          <Row label="Classification">{endpointClassLabel(endpoint.classification)}</Row>
          <Row label="IP addresses">
            {endpoint.ipAddresses.length ? (
              <code className="font-mono text-xs">{endpoint.ipAddresses.join(', ')}</code>
            ) : '—'}
          </Row>
          {endpoint.managedDevice ? (
            <Row label="Managed device">
              <Link to={`/devices/${endpoint.managedDevice.id}`} className="hover:underline">
                {endpoint.managedDevice.name ?? endpoint.managedDevice.id.slice(0, 8)}
              </Link>
            </Row>
          ) : null}
        </tbody>
      </table>
      <h4 className="text-sm font-semibold mb-2">Connected to</h4>
      {links.length === 0 ? (
        <p className="text-sm text-muted-foreground">Not attached anywhere right now.</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {links.map((link) => (
            <li key={link.linkId}>
              <button
                type="button"
                className="w-full text-left rounded-md border border-border-subtle bg-muted/30 px-3 py-2 hover:bg-muted/50"
                onClick={() => { onLinkSelect?.(endpoint.id, link.linkId); }}
              >
                <EndpointLinkSummary link={link} />
              </button>
              <EndpointLinkTraffic endpointId={endpoint.id} link={link} range={trafficRange} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
